import React from 'react';
import { 
  XMarkIcon, 
  BuildingOfficeIcon, 
  MapPinIcon, 
  PhoneIcon, 
  UserGroupIcon,
  StarIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { Accommodation } from '../../types';
import { DateDisplay } from '../../components/Forms';

interface AccommodationDetailsProps {
  accommodation: Accommodation;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
}

const AccommodationDetails: React.FC<AccommodationDetailsProps> = ({ 
  accommodation, 
  isOpen, 
  onClose, 
  onEdit 
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount).replace('SAR', 'ر.س');
  };

  const getRatingStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          />
        ))}
        <span className="text-sm text-gray-600 mr-2">({rating})</span>
      </div>
    );
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-4 h-4 ml-1" />
        نشط
      </span>
    ) : (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
        <ClockIcon className="w-4 h-4 ml-1" />
        غير نشط
      </span>
    );
  };

  const getCityBadge = (city: string) => {
    const colors = {
      'مكة المكرمة': 'bg-gold-100 text-gold-800',
      'المدينة المنورة': 'bg-green-100 text-green-800'
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${colors[city as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        <MapPinIcon className="w-4 h-4 ml-1" />
        {city}
      </span>
    );
  };

  const facilityLabels: Record<string, string> = {
    'parking': 'موقف سيارات',
    'elevator': 'مصعد',
    'reception': 'استقبال 24 ساعة',
    'lobby': 'صالة انتظار',
    'prayer_room': 'مصلى',
    'restaurant': 'مطعم',
    'shop': 'متجر'
  };

  const amenityLabels: Record<string, string> = {
    'AC': 'تكييف',
    'TV': 'تلفزيون',
    'WiFi': 'واي فاي',
    'Fridge': 'ثلاجة',
    'Bathroom': 'حمام خاص',
    'Balcony': 'شرفة',
    'Kitchen': 'مطبخ'
  };

  const roomTypeLabels: Record<string, string> = {
    'family': 'عائلية',
    'men': 'رجال',
    'women': 'نساء'
  };

  const bedTypeLabels: Record<string, string> = {
    'single': 'مفرد',
    'double': 'مزدوج',
    'bunk': 'سرير بطابقين',
    'sofa': 'كنبة'
  };

  const roomStatusLabels: Record<string, string> = {
    'available': 'متاح',
    'occupied': 'مشغول',
    'maintenance': 'صيانة',
    'reserved': 'محجوز'
  };

  const roomStatusColors: Record<string, string> = {
    'available': 'bg-green-100 text-green-800',
    'occupied': 'bg-red-100 text-red-800',
    'maintenance': 'bg-yellow-100 text-yellow-800',
    'reserved': 'bg-blue-100 text-blue-800'
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <BuildingOfficeIcon className="w-6 h-6 text-primary-600 ml-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">{accommodation.name}</h3>
              <div className="flex items-center mt-1 space-x-2 space-x-reverse">
                {getCityBadge(accommodation.location)}
                {getStatusBadge(accommodation.isActive)}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={onEdit}
              className="btn-secondary flex items-center"
            >
              <PencilIcon className="w-4 h-4 ml-1" />
              تعديل
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <UserGroupIcon className="w-8 h-8 text-blue-600" />
                <div className="mr-3">
                  <p className="text-sm text-blue-600">السعة الإجمالية</p>
                  <p className="text-2xl font-bold text-blue-900">{accommodation.totalCapacity || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                <div className="mr-3">
                  <p className="text-sm text-green-600">السعة المتاحة</p>
                  <p className="text-2xl font-bold text-green-900">{accommodation.availableCapacity || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-gold-50 rounded-lg p-4">
              <div className="flex items-center">
                <StarIcon className="w-8 h-8 text-gold-600" />
                <div className="mr-3">
                  <p className="text-sm text-gold-600">التقييم</p>
                  <p className="text-2xl font-bold text-gold-900">{accommodation.rating?.overall || 3}</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <CurrencyDollarIcon className="w-8 h-8 text-purple-600" />
                <div className="mr-3">
                  <p className="text-sm text-purple-600">معدل الإشغال</p>
                  <p className="text-2xl font-bold text-purple-900">{accommodation.occupancyRate || 0}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="w-5 h-5 text-primary-600 ml-2" />
                المعلومات الأساسية
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">اسم المنشأة:</span>
                  <span className="font-medium">{accommodation.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">المدينة:</span>
                  <span className="font-medium">{accommodation.location}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الفرع:</span>
                  <span className="font-medium">{accommodation.branch?.name}</span>
                </div>
                {accommodation.address?.district && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">الحي:</span>
                    <span className="font-medium">{accommodation.address.district}</span>
                  </div>
                )}
                {accommodation.address?.street && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">الشارع:</span>
                    <span className="font-medium">{accommodation.address.street}</span>
                  </div>
                )}
                {accommodation.address?.distanceToHaram && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">المسافة إلى الحرم:</span>
                    <span className="font-medium">{accommodation.address.distanceToHaram} متر</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <PhoneIcon className="w-5 h-5 text-primary-600 ml-2" />
                معلومات الاتصال
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">الهاتف:</span>
                  <span className="font-medium">{accommodation.contact.phone}</span>
                </div>
                {accommodation.contact.mobile && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">الجوال:</span>
                    <span className="font-medium">{accommodation.contact.mobile}</span>
                  </div>
                )}
                {accommodation.contact.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">البريد الإلكتروني:</span>
                    <span className="font-medium">{accommodation.contact.email}</span>
                  </div>
                )}
                {accommodation.contact.manager?.name && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">المدير:</span>
                    <span className="font-medium">{accommodation.contact.manager.name}</span>
                  </div>
                )}
                {accommodation.contact.manager?.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">جوال المدير:</span>
                    <span className="font-medium">{accommodation.contact.manager.phone}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Contract Information */}
          <div className="card">
            <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="w-5 h-5 text-primary-600 ml-2" />
              معلومات العقد
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">تاريخ البداية</p>
                <p className="font-medium"><DateDisplay value={accommodation.contract.startDate} /></p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">تاريخ النهاية</p>
                <p className="font-medium"><DateDisplay value={accommodation.contract.endDate} /></p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">سعر الليلة</p>
                <p className="font-medium">{formatCurrency(accommodation.contract.pricePerNight)}</p>
              </div>
              {accommodation.contract.totalRooms && (
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">إجمالي الغرف</p>
                  <p className="font-medium">{accommodation.contract.totalRooms}</p>
                </div>
              )}
            </div>
          </div>

          {/* Nearby Landmarks */}
          {accommodation.address?.nearbyLandmarks && accommodation.address.nearbyLandmarks.length > 0 && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="w-5 h-5 text-primary-600 ml-2" />
                المعالم القريبة
              </h4>
              <div className="flex flex-wrap gap-2">
                {accommodation.address.nearbyLandmarks.map((landmark, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {landmark}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Facilities */}
          {accommodation.facilities && accommodation.facilities.length > 0 && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4">المرافق والخدمات</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {accommodation.facilities.map((facility, index) => (
                  <div key={index} className="flex items-center p-3 bg-green-50 rounded-lg">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 ml-2" />
                    <span className="text-sm text-green-800">
                      {facilityLabels[facility] || facility}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Services */}
          {accommodation.services && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4">الخدمات الإضافية</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {accommodation.services.meals?.breakfast && (
                    <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <span className="text-orange-800">إفطار</span>
                      <CheckCircleIcon className="w-5 h-5 text-orange-600" />
                    </div>
                  )}
                  {accommodation.services.transportation?.toHaram && (
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-blue-800">نقل للحرم</span>
                      <CheckCircleIcon className="w-5 h-5 text-blue-600" />
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {accommodation.services.laundry?.available && (
                    <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-800">غسيل ملابس</span>
                      <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                    </div>
                  )}
                  {accommodation.services.cleaning?.daily && (
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-green-800">تنظيف يومي</span>
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rating */}
          {accommodation.rating && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <StarIcon className="w-5 h-5 text-primary-600 ml-2" />
                التقييمات
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600 mb-2">التقييم العام</p>
                  {getRatingStars(accommodation.rating.overall || 3)}
                </div>
                {accommodation.rating.cleanliness && (
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 mb-2">النظافة</p>
                    {getRatingStars(accommodation.rating.cleanliness)}
                  </div>
                )}
                {accommodation.rating.location && (
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 mb-2">الموقع</p>
                    {getRatingStars(accommodation.rating.location)}
                  </div>
                )}
                {accommodation.rating.service && (
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600 mb-2">الخدمة</p>
                    {getRatingStars(accommodation.rating.service)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rooms */}
          {accommodation.rooms && accommodation.rooms.length > 0 && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <UserGroupIcon className="w-5 h-5 text-primary-600 ml-2" />
                الغرف ({accommodation.rooms.length})
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">رقم الغرفة</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">النوع</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعة</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">السعر</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الطابق</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">نوع السرير</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {accommodation.rooms.map((room, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{room.roomNumber}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{roomTypeLabels[room.type]}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{room.capacity} شخص</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(room.pricePerPerson)}/شخص</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{room.floor || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{bedTypeLabels[room.bedType || 'single']}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${roomStatusColors[room.status || 'available']}`}>
                            {roomStatusLabels[room.status || 'available']}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistics */}
          {accommodation.stats && (
            <div className="card">
              <h4 className="text-lg font-medium text-gray-900 mb-4">الإحصائيات</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">إجمالي الحجوزات</p>
                  <p className="text-2xl font-bold text-blue-900">{accommodation.stats.totalBookings || 0}</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">إجمالي الإيرادات</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(accommodation.stats.totalRevenue || 0)}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600">متوسط الإشغال</p>
                  <p className="text-2xl font-bold text-purple-900">{accommodation.stats.averageOccupancy || 0}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccommodationDetails; 